import { type Route } from './+types/privacy'

export const meta: Route.MetaFunction = () => {
	const appName = typeof ENV !== 'undefined' ? ENV.APP_NAME : 'InSelect'
	return [
		{ title: `Privacy Policy | ${appName}` },
		{
			name: 'description',
			content: 'Privacy policy and data handling practices for InSelect.',
		},
	]
}

export default function PrivacyRoute() {
	const appName = typeof ENV !== 'undefined' ? ENV.APP_NAME : 'InSelect'

	return (
		<div className="container mx-auto px-4 py-12">
			<div className="prose prose-lg mx-auto max-w-4xl">
				<h1 className="mb-8 text-4xl font-bold">Privacy Policy</h1>
				<p className="text-gray-600">
					Last updated: {new Date().toLocaleDateString()}
				</p>

				<section className="mt-8">
					<h2 className="text-2xl font-semibold">1. Introduction</h2>
					<p>
						Welcome to {appName}'s Privacy Policy. This policy describes how we
						collect, use, and handle your information when you use our services.
					</p>
				</section>

				<section className="mt-8">
					<h2 className="text-2xl font-semibold">2. Information We Collect</h2>
					<h3 className="mt-4 text-xl font-semibold">
						2.1 Information you provide
					</h3>
					<ul className="list-disc pl-6">
						<li>Account information (email, name, profile picture)</li>
						<li>Instagram content and captions</li>
						<li>Brand voice preferences and settings</li>
						<li>
							Payment information (handled securely by our payment processor)
						</li>
					</ul>

					<h3 className="mt-4 text-xl font-semibold">
						2.2 Automatically collected information
					</h3>
					<ul className="list-disc pl-6">
						<li>Usage data and analytics</li>
						<li>Device information</li>
						<li>Log data</li>
						<li>Cookies and similar technologies</li>
					</ul>
				</section>

				<section className="mt-8">
					<h2 className="text-2xl font-semibold">
						3. How We Use Your Information
					</h2>
					<p>We use your information to:</p>
					<ul className="list-disc pl-6">
						<li>Provide and improve our AI caption generation services</li>
						<li>Train our AI models to better understand your brand voice</li>
						<li>Process your transactions</li>
						<li>Send you important updates and communications</li>
						<li>Analyze and improve our services</li>
						<li>Comply with legal obligations</li>
					</ul>
				</section>

				<section className="mt-8">
					<h2 className="text-2xl font-semibold">
						4. Data Storage and Security
					</h2>
					<p>
						We implement industry-standard security measures to protect your
						data. Your information is stored securely in cloud services that
						comply with modern security standards.
					</p>
				</section>

				<section className="mt-8">
					<h2 className="text-2xl font-semibold">
						5. AI Training and Data Usage
					</h2>
					<p>
						Our AI models are trained on your content to provide personalized
						caption suggestions. We ensure that:
					</p>
					<ul className="list-disc pl-6">
						<li>Your data is used only to improve your experience</li>
						<li>We don't share your individual data with other users</li>
						<li>You can request deletion of your training data</li>
						<li>AI models are regularly updated for accuracy and security</li>
					</ul>
				</section>

				<section className="mt-8">
					<h2 className="text-2xl font-semibold">6. Your Rights</h2>
					<p>You have the right to:</p>
					<ul className="list-disc pl-6">
						<li>Access your personal data</li>
						<li>Correct inaccurate data</li>
						<li>Request data deletion</li>
						<li>Export your data</li>
						<li>Opt-out of certain data processing</li>
					</ul>
				</section>

				<section className="mt-8">
					<h2 className="text-2xl font-semibold">7. Third-Party Services</h2>
					<p>We use trusted third-party services for specific functions:</p>
					<ul className="list-disc pl-6">
						<li>Payment processing</li>
						<li>Analytics</li>
						<li>Cloud storage</li>
						<li>Email communications</li>
					</ul>
				</section>

				<section className="mt-8">
					<h2 className="text-2xl font-semibold">8. Updates to This Policy</h2>
					<p>
						We may update this privacy policy from time to time. We will notify
						you of any significant changes via email or through our platform.
					</p>
				</section>

				<section className="mt-8">
					<h2 className="text-2xl font-semibold">9. Contact Us</h2>
					<p>
						If you have any questions about this privacy policy or our data
						practices, please contact us at:
					</p>
					<ul className="list-none pl-6">
						<li>Email: hello@inselect.app</li>
					</ul>
				</section>

				<footer className="mt-12 border-t pt-8 text-sm text-gray-600">
					<p>
						© {new Date().getFullYear()} {appName}. All rights reserved.
					</p>
				</footer>
			</div>
		</div>
	)
}
